import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { needsConsent, refusalFor } from '../../lib/social'
import { sharePost, socialStatus, updateConsent } from '../../services/social'
import Button from '../common/Button'

/**
 * «ارسال به فید» under a result (design page 8; P-core-4 of the go-live
 * plan). The server refuses the first share with `consent_required`; then
 * the modal shows the server's own consent text and, on «قبول دارم»,
 * records the consent and repeats the very same share. Sharing is
 * idempotent on the server, so a second tap is harmless.
 */
export default function ShareToFeed({ attemptId, className = '' }) {
  const { t } = useTranslation('social')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)
  const [consent, setConsent] = useState(null)
  const [shared, setShared] = useState(false)

  async function share() {
    if (busy || !attemptId) return
    setBusy(true)
    setNotice(null)
    try {
      await sharePost(attemptId)
      setShared(true)
      setNotice({ good: true, text: t('shareToFeed.sentNotice') })
    } catch (error) {
      if (needsConsent(error)) {
        const status = await socialStatus().catch(() => null)
        setConsent({ text: status?.consentText?.trim() || t('shareToFeed.fallbackConsent') })
      } else {
        setNotice({ good: false, text: refusalFor(error, 'share') })
      }
    } finally {
      setBusy(false)
    }
  }

  async function agree() {
    setConsent(null)
    setBusy(true)
    try {
      await updateConsent(true)
    } catch (error) {
      setNotice({ good: false, text: refusalFor(error, 'share') })
      setBusy(false)
      return
    }
    setBusy(false)
    await share()
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <Button variant="secondary" className="w-full" loading={busy} disabled={!attemptId} onClick={share}>
        {shared ? t('shareToFeed.sendAgain') : t('shareToFeed.send')}
      </Button>
      {notice && (
        <p role="status" className={`rounded-xl px-3 py-2 text-xs font-semibold ${notice.good ? 'bg-good-tint text-good-ink' : 'bg-bad-tint text-bad-ink'}`}>
          {notice.text}
          {notice.good && (
            <>
              {' '}
              <Link to="/social" className="underline">
                {t('shareToFeed.viewFeed')}
              </Link>
            </>
          )}
        </p>
      )}
      {consent && <ConsentDialog text={consent.text} onAgree={agree} onCancel={() => setConsent(null)} />}
    </div>
  )
}

/** The consent text verbatim, two buttons; the scrim swallows taps so nothing underneath is pressed. */
export function ConsentDialog({ text, onAgree, onCancel }) {
  const { t } = useTranslation('social')
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/55 p-6" role="presentation" onClick={onCancel}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="feed-consent-title"
        className="w-full max-w-sm space-y-4 rounded-[22px] bg-surface p-5 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="feed-consent-title" className="text-base font-bold text-ink">
          {t('shareToFeed.consentTitle')}
        </h2>
        <p className="text-[13px] leading-[1.9] text-ink-2">{text}</p>
        <div className="flex gap-2">
          <Button variant="ghost" className="flex-1" onClick={onCancel}>
            {t('shareToFeed.consentCancel')}
          </Button>
          <Button className="flex-1" onClick={onAgree}>
            {t('shareToFeed.consentAgree')}
          </Button>
        </div>
      </div>
    </div>
  )
}
